import loggerFactory from '../../logger';
import { x, isEqual } from '../../utils';

const log = loggerFactory.get('repository:diff');

const identifierDelimiter = '#';
const attributeDelimiter = '.';

function updater(rootObj, fullKey, idKey) {
    return applyLast(rootObj, idKey, fullKey, function (obj, key) {
        return function (value) {
            if (!obj) {
                log.warn(`obj is null for fullKey: ${fullKey}`);
                return value;
            } else if (!(obj instanceof Object)) {
                log.info(`obj is not an object: ${obj} for fullKey: ${fullKey}`);
                return obj;
            }
            obj[key] = value;
            return value;
        };
    });
}

function retriever(rootObj, fullKey, idKey) {
    return applyLast(rootObj, idKey, fullKey, function (obj, key) {
        return function () {
            if (hasIdentifierDelimiter(key)) {
                const split = splitOnIdentifierDelimiter(key);
                return getItem(obj[split.field], idKey, split.id);
            }
            return obj[key];
        };
    });
}

function remover(rootObj, fullKey, idKey) {
    return applyLast(rootObj, idKey, fullKey, function (obj, key) {
        if (!obj) {
            log.warn(`Unsupported remove on obj (${fullKey}, ${idKey})`);
            return function remove() {
                // Dont do anything as we cant remove this item.
            };
        }
        if (!x.isObject(obj) || Array.isArray(obj)) {
            throw new Error(`Unsupported changeset diff (${fullKey}, ${idKey})`);
        }
        return function remove(valueToRemove) {
            if (hasIdentifierDelimiter(key)) {
                removeWithIdentifier(obj, key, idKey);
            } else {
                removeWithoutIdentifier(obj, key, valueToRemove);
            }
        };
    });
}

function reorder(rootObj, fullKey, idKey) {
    return applyLast(rootObj, idKey, fullKey, function (obj, key) {
        return function move(changes) {
            if (!obj) {
                log.warn(`Unable to move item with key ${key} on obj, obj is undefined`);
                return;
            }
            const targetObject = obj[key];
            if (Array.isArray(targetObject)) {
                const descriptors = changes.map(change => ({
                    ...change,
                    value: targetObject[change.from],
                }));
                const reorderedItems = targetObject.filter(
                    (item, index) => !changes.some(change => change.from === index)
                );
                descriptors.sort((d1, d2) => d1.to - d2.to);
                descriptors.forEach(descriptor => {
                    const { to, value } = descriptor;
                    reorderedItems.splice(to, 0, value);
                });
                obj[key] = reorderedItems;
            } else {
                throw new Error(`Cannot move item on something that is not an array.${obj},${key}`);
            }
        };
    });
}

function removeWithIdentifier(obj, key, idKey) {
    const split = splitOnIdentifierDelimiter(key);
    const targetObject = obj[split.field];

    if (Array.isArray(targetObject)) {
        removeArrayItem(targetObject, idKey, split.id);
    } else if (x.isObject(targetObject) && targetObject.hasOwnProperty(split.id)) {
        delete targetObject[split.id];
    } else {
        throw new Error(`Property ${split.id} does not exist on object (${idKey})`);
    }
}

function removeWithoutIdentifier(obj, key, valueToRemove) {
    const targetObject = obj[key];
    if (!obj.hasOwnProperty(key)) {
        throw new Error('Unsupported changeset diff');
    } else if (Array.isArray(targetObject) && valueToRemove) {
        for (let i = 0; i < targetObject.length; i += 1) {
            if (isEqual(valueToRemove, targetObject[i])) {
                targetObject.splice(i, 1);
                break;
            }
        }
    } else {
        delete obj[key];
    }
}

function adder(rootObj, fullKey, idKey) {
    return applyLast(rootObj, idKey, fullKey, function (obj, key) {
        return function (value, index) {
            if (!obj) {
                log.warn(
                    `Unable to add property with key ${key} on obj, obj is undefined, value being ${value}`
                );
                return;
            }
            const targetObject = obj[key];
            if (Array.isArray(targetObject)) {
                if (index === null || index === undefined) {
                    targetObject.push(value);
                } else {
                    targetObject.splice(index, 0, value);
                }
            } else if (hasIdentifierDelimiter(key)) {
                const split = splitOnIdentifierDelimiter(key);
                const valueToUpdate = obj[split.field];
                if (x.isObject(valueToUpdate) && !valueToUpdate.hasOwnProperty(split.id)) {
                    valueToUpdate[split.id] = value;
                } else {
                    throw new Error(
                        'Cannot add a property that already exists. Changeset should be of type modified.'
                    );
                }
            } else {
                if (obj.hasOwnProperty(key) && obj[key] !== null && obj[key] !== undefined) {
                    throw new Error(
                        `Cannot add a property that already exists. Changeset should be of type modified.${obj},${key}`
                    );
                }
                obj[key] = value;
            }
        };
    });
}

function find(arr, compareFunction) {
    return arr.filter(compareFunction);
}

function first(arr) {
    return arr[0];
}

function compare(idKey, id) {
    return function (item) {
        // this should be == and not ===
        // the case is when we compare ids e.g 2 == '2'

        return item[idKey] == id; // eslint-disable-line eqeqeq
    };
}

function getItem(arr, idKey, id) {
    return first(find(arr, compare(idKey, id)));
}

function removeArrayItem(arr, idKey, id) {
    function remove(arr2, item2) {
        const index = arr2.indexOf(item2);
        if (index !== -1) {
            arr2.splice(index, 1);
        }
    }

    const item = first(find(arr, compare(idKey, id)));
    remove(arr, item);
}

function hasIdentifierDelimiter(key) {
    return key.indexOf(identifierDelimiter) >= 0;
}

function splitOnIdentifierDelimiter(key) {
    const split = key.split(identifierDelimiter);
    return {
        field: split[0],
        id: split[1],
    };
}

function applyLast(rootObj, idKey, fullKey, applier) {
    return fullKey.split(attributeDelimiter).reduce(function (obj, key, i, arr) {
        // last level, return apply function
        if (i === arr.length - 1) {
            return applier(obj, key);
        }

        // try to find list entity
        if (hasIdentifierDelimiter(key)) {
            const split = splitOnIdentifierDelimiter(key);
            return getItem(obj[split.field], idKey, split.id);
        }

        // dig deeper
        return obj[key];
    }, rootObj);
}

export default {
    updater,
    retriever,
    remover,
    adder,
    reorder,
};
